<script setup lang="ts">
import { computed } from 'vue';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import StarWithoutBgIcon from '@/assets/svg/conversations/star_without_bg.svg?component';
import {
  getApplicationScoreBadgeTone,
  hasApplicationScore,
} from '@/core/conversations/application-score/application-score.util';

const props = withDefaults(
  defineProps<{
    score?: number | null;
    jobTitle?: string;
    hideJobTitle?: boolean;
    notScoredBgClass?: string;
    /** 700 score and "Not scored" labels for the candidate card; every other usage stays 600. */
    bold?: boolean;
  }>(),
  {
    jobTitle: '',
    hideJobTitle: false,
    notScoredBgClass: 'bg-tint-60',
    bold: false,
  },
);

const hasScore = computed(() => hasApplicationScore(props.score));
const formattedScore = computed(() =>
  hasScore.value ? ApplicationScoreService.formatScore(props.score) : '',
);
const compactBadgeBackgroundClass = computed(() => {
  if (!hasScore.value) {
    return props.notScoredBgClass;
  }

  switch (getApplicationScoreBadgeTone(props.score as number)) {
    case 'positive':
      return 'bg-primary-300';
    case 'warning':
      return 'bg-caution-300';
    default:
      return 'bg-critical-400';
  }
});
</script>

<template>
  <div class="inline-flex min-w-0 items-center">
    <div
      v-if="hasScore"
      class="flex items-center gap-0.5 rounded-sm px-1 py-0.5"
      :class="compactBadgeBackgroundClass"
    >
      <StarWithoutBgIcon alt="Star" class="h-3 w-3 text-shade-900" />
      <div class="text-xs text-shade-880" :class="bold ? 'font-bold' : 'font-semibold'">
        {{ formattedScore }}
      </div>
    </div>
    <div
      v-else
      class="rounded-sm px-1 py-0.5 text-xs text-shade-840"
      :class="[compactBadgeBackgroundClass, bold ? 'font-bold' : 'font-semibold']"
    >
      Not scored
    </div>
    <template v-if="jobTitle && !hideJobTitle">
      <div class="ml-2 mr-1 text-xs text-shade-880">for</div>
      <div
        class="compact-applicant-title mr-0.5 min-w-0 truncate text-xs font-bold text-shade-880"
        :title="jobTitle"
      >
        {{ jobTitle }}
      </div>
    </template>
  </div>
</template>

<style lang="postcss" scoped>
.compact-applicant-title {
  max-width: 188px;
}
</style>
